/**
 * Blast Range Rules — new structure aligned with Figma "New Config Screens" redesign.
 *
 * Key:   `${format}_${position}_${stack}`
 *
 * Each entry describes the full config-screen hierarchy for that scenario:
 *
 *   primaryLabel    – section heading shown above the primary buttons
 *   primaryRowBreak – index BEFORE which to insert a visual separator (null = none)
 *   primaryActions  – array of { key, label } buttons
 *
 *   secondary       – keyed by primaryAction.key
 *     null            = no secondary section
 *     object          = secondary section with:
 *       label         – section heading
 *       isOptional    – if true, Continue is allowed without a selection
 *       subtitle      – small text shown under the heading (or null)
 *       options       – array of { key, label, csvDir?, tertiary? }
 *         csvDir      – non-null = this secondary option loads a DIFFERENT CSV
 *         tertiary    – nested { label, isOptional, options: [{key, label}] }
 *
 * ── Primary action key conventions ───────────────────────────────────────────
 *   'open_raise'   – hero opens with a raise
 *   'open_jam'     – hero opens with an all-in shove
 *   'open_limp'    – hero limps / completes (SB only)
 *   'open_mixed'   – view the full mixed opening range
 *   '<csvDir>'     – reaction scenario; the key IS the CSV directory name
 *
 * ── Secondary / tertiary key conventions ─────────────────────────────────────
 *   Filter keys (no CSV change):
 *     'jam', 'call', 'mixed', 'check', 'raise_large', 'raise_small', 'raise'
 *   CSV-change keys (for HU SB BB Response Types):
 *     'sb_vs_sb_raise_vs_bb_3b_small', 'sb_vs_sb_raise_vs_bb_3b_large', 'sb_vs_sb_open_bb_ai'
 *
 * ── Missing / stub CSVs ───────────────────────────────────────────────────────
 *   These scenarios are shown in the Figma but CSV files are not yet present:
 *   - hu_BB_25 vs SB Jam (bb_vs_sb_ai_25bb) — only up to 15bb
 *   See CSV_AUDIT.md for the full list of known data gaps.
 *
 * ── 3-way scenario renames (2026-07) ──────────────────────────────────────────
 *   The source range images were re-organized: some folders were renamed to
 *   correct the naming convention, some were dropped entirely because the
 *   situation doesn't occur given this game's action order (BTN acts, then
 *   SB, then BB — so e.g. "BTN facing SB's jam" only makes sense once BTN has
 *   opened first). CSV references below were updated to match:
 *   - bb_vs_sb_ai now HAS data (4,5,7,9,10,12,15bb) — no longer a stub.
 *   - bb_vs_sb_ai_vs_btn_open → bb_vs_btn_open_vs_sb_ai (chronological order fix)
 *   - btn_vs_sb_ai → btn_vs_btn_open_vs_sb_ai
 *   - btn_call_vs_btn_open_vs_bb_3b_jam → btn_vs_btn_open_vs_bb_ai
 *   - btn_vs_btn_open_vs_sb_3b_vs_bb_call → btn_vs_btn_open_vs_bb_3b (closest
 *     surviving equivalent; the original 3-villain-action combo was dropped)
 *   - sb_vs_bb_ai — dropped, doesn't occur (SB acts before BB, so SB can't be
 *     reacting to a BB jam without an intervening BTN/BB action already
 *     covered elsewhere); the "BB Jam" response option under SB's Raise is
 *     disabled until/unless this situation is reintroduced with real images.
 *   - sb_vs_sb_call_vs_bb_raise → split into sb_vs_sb_call_vs_bb_raise_large /
 *     _small (now has data at every stack, so the single-size variant is gone)
 *   - bb_vs_sb_call_vs_bb_raise_vs_sb_ai → split into
 *     bb_vs_sb_call_vs_bb_raise_large_vs_sb_ai / _small_vs_sb_ai (same reason)
 *
 * ── HU scenario renames (2026-07-09) ──────────────────────────────────────────
 *   Standardized on the longer, fully-qualified naming convention (matches
 *   what the source range images already used) instead of the shorter CSV
 *   names, to eliminate CSV/image folder-name drift that broke the
 *   range-check.html audit tool. CSV dirs, blastRangeRules.js keys/csvDirs,
 *   and batch_process.py's canonical scenario list were all updated together:
 *   - sb_limp_vs_bb_jam → sb_vs_sb_limp_vs_bb_jam
 *   - sb_vs_bb_3b_large → sb_vs_sb_raise_vs_bb_3b_large
 *   - sb_vs_bb_3b_small → sb_vs_sb_raise_vs_bb_3b_small
 */

// ─── Shared option lists ──────────────────────────────────────────────────────

/** Jam / Call / Mixed — used for "Your Response" and basic reaction secondaries */
const JAM_CALL_MIXED = [
  { key: 'jam',   label: 'Jam'   },
  { key: 'call',  label: 'Call'  },
  { key: 'mixed', label: 'Mixed' },
];

/** BB Option Type with two raise sizes (most stacks) */
const BB_OPTION_2SIZES = [
  { key: 'jam',         label: 'Jam'        },
  { key: 'raise_large', label: 'Large Raise' },
  { key: 'raise_small', label: 'Small Raise' },
  { key: 'check',       label: 'Check'       },
  { key: 'mixed',       label: 'Mixed'       },
];

/** BB response when facing SB open/raise — Call (not Check) as BB can call or 3bet */
const BB_OPTION_RAISE = [
  { key: 'jam',         label: 'Jam'        },
  { key: 'raise_large', label: 'Large Raise' },
  { key: 'raise_small', label: 'Small Raise' },
  { key: 'call',        label: 'Call'        },
  { key: 'mixed',       label: 'Mixed'       },
];

// ─── Secondary factory helpers ────────────────────────────────────────────────

const yourResponse = (opts = JAM_CALL_MIXED) => ({
  label: 'Your Response', isOptional: false, subtitle: null, options: opts,
});

const optionType = (opts = BB_OPTION_2SIZES) => ({
  label: 'Option Type', isOptional: true, subtitle: null, options: opts,
});

const bbResponseTypes = (opts) => ({
  label: "BB's Response", isOptional: true,
  subtitle: 'How BB reacts to your open — select to study your counter', options: opts,
});

/** Generic "Response Types" secondary — e.g. BTN facing villain reaction scenarios */
const responseTypes = (opts) => ({
  label: 'Response Types', isOptional: true,
  subtitle: 'Filter your action to specific situations.', options: opts,
});

// ─── 3-Way SB Response Type option sets ──────────────────────────────────────

/** SB jams open → BTN re-jams (raises) → SB must respond. CSV: sb_vs_btn_open */
const SB_JAM_RESPONSE_TYPES = responseTypes([
  { key: 'sb_btn_raise', label: 'BTN Raise', csvDir: 'sb_vs_btn_open', tertiary: null },
]);

/**
 * SB raises → BB jams → SB must respond.
 * Disabled: sb_vs_bb_ai images were removed (this action order doesn't occur
 * — see "3-way scenario renames" note above). No options until re-added.
 */
const SB_RAISE_RESPONSE_TYPES = null;

/** SB limps → BB reacts → SB responds (7bb/9bb: two raise sizes). */
const SB_LIMP_RESPONSE_TYPES_7_9 = responseTypes([
  { key: 'sb_limp_bb_jam',         label: 'BB Jam',         csvDir: 'sb_vs_sb_call_vs_bb_ai',          tertiary: null          },
  { key: 'sb_limp_bb_raise_large', label: 'BB Large Raise', csvDir: 'sb_vs_sb_call_vs_bb_raise_large', tertiary: yourResponse() },
  { key: 'sb_limp_bb_raise_small', label: 'BB Small Raise', csvDir: 'sb_vs_sb_call_vs_bb_raise_small', tertiary: yourResponse() },
]);

/**
 * SB limps → BB reacts → SB responds (12bb/15bb).
 * Used to be a single "BB Raise" option (sb_vs_sb_call_vs_bb_raise), but that
 * CSV was dropped in favor of explicit large/small versions (now have data at
 * every stack) — so this is now identical in shape to the 7bb/9bb version.
 */
const SB_LIMP_RESPONSE_TYPES_12_15 = responseTypes([
  { key: 'sb_limp_bb_jam',         label: 'BB Jam',         csvDir: 'sb_vs_sb_call_vs_bb_ai',          tertiary: null          },
  { key: 'sb_limp_bb_raise_large', label: 'BB Large Raise', csvDir: 'sb_vs_sb_call_vs_bb_raise_large', tertiary: yourResponse() },
  { key: 'sb_limp_bb_raise_small', label: 'BB Small Raise', csvDir: 'sb_vs_sb_call_vs_bb_raise_small', tertiary: yourResponse() },
]);

// ─── 3-Way BTN Response Type options (secondary under open_raise) ─────────────

/**
 * 15bb: SB Jam, BB Jam, BB 3bet — all change the loaded CSV.
 *
 * 'bb_jam' = BTN opens, SB folds, BB jams directly over the open. CSV:
 * btn_vs_btn_open_vs_bb_ai (renamed from btn_call_vs_btn_open_vs_bb_3b_jam —
 * see "3-way scenario renames" note above).
 * The separate "BTN open → SB calls → BB jams" spot lives at
 * btn_open_vs_sb_call_vs_bb_3b_jam and is not currently wired to any UI option
 * (that folder's images were removed in the same rename pass, so it no
 * longer has a CSV at all).
 */
const BTN_15_RESPONSE_TYPES = responseTypes([
  { key: 'sb_jam',  label: 'SB Jam',  csvDir: 'btn_vs_btn_open_vs_sb_ai', tertiary: null          },
  { key: 'bb_jam',  label: 'BB Jam',  csvDir: 'btn_vs_btn_open_vs_bb_ai', tertiary: null          },
  { key: 'bb_3bet', label: 'BB 3bet', csvDir: 'btn_vs_btn_open_vs_bb_3b', tertiary: yourResponse() },
]);

/**
 * 12bb: SB Jam, BB Jam (both have 12bb data). BB 3bet is deliberately omitted
 * here — its CSV (btn_vs_btn_open_vs_bb_3b) still only has 15bb data (same gap
 * that existed before the rename, just carried over), so offering it at 12bb
 * used to let a Fixed Hands quiz select this stack, 404 on the missing CSV,
 * and silently render that as a fake 100% Fold (same failure mode as the
 * SB Jam vs BTN Open / 9-3bb bug — see buildFixedHandsConfig's availableStacks
 * filter). Re-add once a real btn_vs_btn_open_vs_bb_3b_12bb.csv exists.
 */
const BTN_12_RESPONSE_TYPES = responseTypes([
  { key: 'sb_jam',  label: 'SB Jam',  csvDir: 'btn_vs_btn_open_vs_sb_ai', tertiary: null          },
  { key: 'bb_jam',  label: 'BB Jam',  csvDir: 'btn_vs_btn_open_vs_bb_ai', tertiary: null          },
]);

// ─── HU SB secondary options that CHANGE the CSV loaded ──────────────────────

/** BB jams over SB's raise open */
const HU_SB_BB_JAM = {
  key: 'sb_vs_sb_open_bb_ai', label: 'Jam', csvDir: 'sb_vs_sb_open_bb_ai',
  tertiary: null,
};
const HU_SB_3B_LARGE = {
  key: 'sb_vs_sb_raise_vs_bb_3b_large', label: '3bet Large', csvDir: 'sb_vs_sb_raise_vs_bb_3b_large',
  tertiary: yourResponse(),
};
const HU_SB_3B_SMALL = {
  key: 'sb_vs_sb_raise_vs_bb_3b_small', label: '3bet Small', csvDir: 'sb_vs_sb_raise_vs_bb_3b_small',
  tertiary: yourResponse(),
};
/** Single 3bet size (used at 15bb where no Large/Small distinction exists) */
const HU_SB_3B = {
  key: 'sb_vs_sb_raise_vs_bb_3b_small', label: '3bet', csvDir: 'sb_vs_sb_raise_vs_bb_3b_small',
  tertiary: yourResponse(),
};
/** BB raises large over SB's open — stub, CSV pending */
const HU_SB_RAISE_LARGE = {
  key: 'sb_vs_bb_raise_large', label: 'Large Raise', csvDir: 'sb_vs_bb_raise_large',
  tertiary: yourResponse(),
};
/** BB raises small over SB's open — stub, CSV pending */
const HU_SB_RAISE_SMALL = {
  key: 'sb_vs_bb_raise_small', label: 'Small Raise', csvDir: 'sb_vs_bb_raise_small',
  tertiary: yourResponse(),
};

// ─── HU SB limp response options (BB reacts to SB limp) ─────────────────────

/** BB jams over SB's limp */
const HU_SB_LIMP_BB_JAM = {
  key: 'sb_vs_sb_limp_vs_bb_jam', label: 'Jam', csvDir: 'sb_vs_sb_limp_vs_bb_jam',
  tertiary: null,
};
/** BB raises large over SB's limp */
const HU_SB_LIMP_RAISE_LARGE = {
  key: 'sb_vs_sb_call_vs_bb_raise_large', label: 'Large Raise', csvDir: 'sb_vs_sb_call_vs_bb_raise_large',
  tertiary: yourResponse(),
};
/** BB raises small over SB's limp */
const HU_SB_LIMP_RAISE_SMALL = {
  key: 'sb_vs_sb_call_vs_bb_raise_small', label: 'Small Raise', csvDir: 'sb_vs_sb_call_vs_bb_raise_small',
  tertiary: yourResponse(),
};
// NOTE: HU_SB_LIMP_RAISE (single-size alias) removed — all stacks now use Large/Small distinction.

/** BB Response Types for limp scenarios — "Filter your action" subtitle */
const bbLimpResponseTypes = (opts) => ({
  label: "BB's Response", isOptional: true,
  subtitle: 'Filter your action to specific situations.', options: opts,
});

// ─── HU BB limp tertiary — SB jams after BB raised ───────────────────────────

/**
 * When BB raised large vs SB limp and SB jams back, BB uses this tertiary
 * to load the specific call-vs-jam CSV.
 */
const BB_RAISE_LARGE_SB_JAM_TERTIARY = {
  label: 'SB Response', isOptional: true, subtitle: null,
  options: [
    { key: 'sb_jam', label: 'SB Jams', csvDir: 'bb_vs_sb_call_vs_bb_raise_large_vs_sb_ai', tertiary: null },
  ],
};
/** Same as above but for BB's small raise. */
const BB_RAISE_SMALL_SB_JAM_TERTIARY = {
  label: 'SB Response', isOptional: true, subtitle: null,
  options: [
    { key: 'sb_jam', label: 'SB Jams', csvDir: 'bb_vs_sb_call_vs_bb_raise_small_vs_sb_ai', tertiary: null },
  ],
};

/** BB Option Type with two raise sizes — HU version with SB jam tertiary */
const BB_OPTION_2SIZES_HU = [
  { key: 'jam',         label: 'Jam'         },
  { key: 'raise_large', label: 'Large Raise', tertiary: BB_RAISE_LARGE_SB_JAM_TERTIARY },
  { key: 'raise_small', label: 'Small Raise', tertiary: BB_RAISE_SMALL_SB_JAM_TERTIARY },
  { key: 'check',       label: 'Check'        },
  { key: 'mixed',       label: 'Mixed'        },
];

// ─── 3-Way BB limp tertiary — SB jams after BB raised ────────────────────────

/** 3-way 7bb/9bb: BB raised large vs SB limp, SB jams back */
const BB_3WAY_RAISE_LARGE_SB_JAM_TERTIARY = {
  label: 'SB Response', isOptional: true, subtitle: null,
  options: [
    { key: 'sb_jam', label: 'SB Jams', csvDir: 'bb_vs_sb_call_vs_bb_raise_large_vs_sb_ai', tertiary: null },
  ],
};
/** 3-way 7bb/9bb: BB raised small vs SB limp, SB jams back */
const BB_3WAY_RAISE_SMALL_SB_JAM_TERTIARY = {
  label: 'SB Response', isOptional: true, subtitle: null,
  options: [
    { key: 'sb_jam', label: 'SB Jams', csvDir: 'bb_vs_sb_call_vs_bb_raise_small_vs_sb_ai', tertiary: null },
  ],
};
/** BB Option Type with two raise sizes — 3-way 7bb/9bb version with SB jam tertiary */
const BB_OPTION_2SIZES_3WAY_7_9 = [
  { key: 'jam',         label: 'Jam'        },
  { key: 'raise_large', label: 'Large Raise', tertiary: BB_3WAY_RAISE_LARGE_SB_JAM_TERTIARY },
  { key: 'raise_small', label: 'Small Raise', tertiary: BB_3WAY_RAISE_SMALL_SB_JAM_TERTIARY },
  { key: 'check',       label: 'Check'       },
  { key: 'mixed',       label: 'Mixed'       },
];

/** 3-way 7bb: BB has only one raise size vs SB limp, SB jams back */
const BB_3WAY_RAISE_SINGLE_SB_JAM_TERTIARY = {
  label: 'SB Response', isOptional: true, subtitle: null,
  options: [
    { key: 'sb_jam', label: 'SB Jams', csvDir: 'bb_vs_sb_call_vs_bb_raise_small_vs_sb_ai', tertiary: null },
  ],
};
/** BB Option Type with one raise size (3way BB 7bb) */
const BB_OPTION_1SIZE = [
  { key: 'jam',   label: 'Jam'   },
  { key: 'raise', label: 'Raise', tertiary: BB_3WAY_RAISE_SINGLE_SB_JAM_TERTIARY },
  { key: 'check', label: 'Check' },
  { key: 'mixed', label: 'Mixed' },
];

/**
 * BB Option Type with two raise sizes — 3-way 12bb/15bb version.
 * Used to point at a shared "single raise size" SB-jam tertiary, but that
 * CSV (bb_vs_sb_call_vs_bb_raise_vs_sb_ai) was dropped in favor of explicit
 * large/small versions (which now have data at every stack) — so this just
 * reuses the same large/small tertiaries as the 7bb/9bb version.
 */
const BB_OPTION_2SIZES_3WAY_12_15 = [
  { key: 'jam',         label: 'Jam'        },
  { key: 'raise_large', label: 'Large Raise', tertiary: BB_3WAY_RAISE_LARGE_SB_JAM_TERTIARY },
  { key: 'raise_small', label: 'Small Raise', tertiary: BB_3WAY_RAISE_SMALL_SB_JAM_TERTIARY },
  { key: 'check',       label: 'Check'       },
  { key: 'mixed',       label: 'Mixed'       },
];

/** BB faces SB 4bet jam — secondary asks what BB's 3bet size was (changes CSV loaded) */
const yourThreeBetSize = (largeCsvDir, smallCsvDir) => ({
  label: 'Your 3bet Size', isOptional: false, subtitle: null,
  options: [
    { key: '3bet_large', label: 'Large', csvDir: largeCsvDir, tertiary: null },
    { key: '3bet_small', label: 'Small', csvDir: smallCsvDir, tertiary: null },
  ],
});

/** BB faces SB 3bet jam — secondary asks what BB's initial raise size was (changes CSV loaded) */
const yourInitialRaiseSize = (largeCsvDir, smallCsvDir) => ({
  label: 'Your Initial Raise Size', isOptional: false, subtitle: null,
  options: [
    { key: 'raise_large', label: 'Large', csvDir: largeCsvDir, tertiary: null },
    { key: 'raise_small', label: 'Small', csvDir: smallCsvDir, tertiary: null },
  ],
});

// ─── BLAST_RANGE_RULES ────────────────────────────────────────────────────────

export const BLAST_RANGE_RULES = {

  // ── 3-Way BTN ──────────────────────────────────────────────────────────────
  // btn_open (4,6,9bb: allin only; 12,15bb: raise+allin)
  // btn_vs_sb_ai (12,15bb: call)
  // Multi-action files (15bb): sb_3b+bb_ai, sb_3b+bb_call, sb_ai+bb_call, sb_call+bb_ai
  // Multi-action files (12bb): sb_ai+bb_call

  '3way_BTN_15': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [
      { key: 'open_jam',   label: 'Jam'   },
      { key: 'open_raise', label: 'Raise' },
      { key: 'open_mixed', label: 'Mixed' },
    ],
    secondary: {
      open_raise: BTN_15_RESPONSE_TYPES,
      open_jam:   null,
      open_mixed: null,
    },
  },

  '3way_BTN_12': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [
      { key: 'open_jam',   label: 'Jam'   },
      { key: 'open_raise', label: 'Raise' },
      { key: 'open_mixed', label: 'Mixed' },
    ],
    secondary: {
      open_raise: BTN_12_RESPONSE_TYPES,
      open_jam:   null,
      open_mixed: null,
    },
  },

  // Jam-only stacks — auto-select open_jam
  '3way_BTN_9': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [{ key: 'open_jam', label: 'Jam' }],
    secondary: { open_jam: null },
  },
  '3way_BTN_6': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [{ key: 'open_jam', label: 'Jam' }],
    secondary: { open_jam: null },
  },
  '3way_BTN_4': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [{ key: 'open_jam', label: 'Jam' }],
    secondary: { open_jam: null },
  },

  // ── 3-Way SB ──────────────────────────────────────────────────────────────
  // sb_open        (3,6,7,9,12,15bb): SB opening range
  // sb_vs_btn_open (12,15bb):  SB jams BTN's open → BTN Raise response type
  // sb_vs_btn_ai   (3,6,9,12,15bb): SB calls BTN's jam → Call vs BTN Jam action
  // sb_vs_bb_ai    (12,15bb):  SB raises, BB jams → BB Jam response type
  // sb_limp_vs_bb_* (future):  SB limps, BB reacts (7,9bb response types)
  //
  // FIXED (2026-07-05): sb_open_12bb.csv / sb_open_15bb.csv were previously
  // missing every 'raise' row (regenerated from source images in
  // "Blast Ranges (image files)/3_way/sb_open/" per range_image_analysis_rules.pdf
  // — the prior CSVs predated the raise_small/raise_large color entries and had
  // silently nearest-neighbored every raise-colored cell into 'allin'). Now
  // correctly has raise_2 (12bb) / raise_2.25 (15bb) rows.

  '3way_SB_15': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: 4,   // row 1: Jam/Raise/Limp/Mixed | row 2: Call vs BTN Jam
    primaryActions: [
      { key: 'open_jam',     label: 'Jam'             },
      { key: 'open_raise',   label: 'Raise'           },
      { key: 'open_limp',    label: 'Limp'            },
      { key: 'open_mixed',   label: 'Mixed'           },
      { key: 'sb_vs_btn_ai', label: 'Call vs BTN Jam' },
    ],
    secondary: {
      open_jam:     SB_JAM_RESPONSE_TYPES,          // BTN Raise — sb_vs_btn_open_15bb ✓
      open_raise:   SB_RAISE_RESPONSE_TYPES,        // disabled — sb_vs_bb_ai removed
      open_limp:    SB_LIMP_RESPONSE_TYPES_12_15,   // BB Jam | BB Large Raise | BB Small Raise
      open_mixed:   null,
      sb_vs_btn_ai: null,
    },
  },

  '3way_SB_12': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: 4,
    primaryActions: [
      { key: 'open_jam',     label: 'Jam'             },
      { key: 'open_raise',   label: 'Raise'           },
      { key: 'open_limp',    label: 'Limp'            },
      { key: 'open_mixed',   label: 'Mixed'           },
      { key: 'sb_vs_btn_ai', label: 'Call vs BTN Jam' },
    ],
    secondary: {
      open_jam:     SB_JAM_RESPONSE_TYPES,          // BTN Raise — sb_vs_btn_open_12bb ✓
      open_raise:   SB_RAISE_RESPONSE_TYPES,        // disabled — sb_vs_bb_ai removed
      open_limp:    SB_LIMP_RESPONSE_TYPES_12_15,   // BB Jam | BB Large Raise | BB Small Raise
      open_mixed:   null,
      sb_vs_btn_ai: null,
    },
  },

  // Jam + Limp + Mixed stacks — no Raise CSV exists
  '3way_SB_9': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: 3,   // row 1: Jam/Limp/Mixed | row 2: Call vs BTN Jam
    primaryActions: [
      { key: 'open_jam',     label: 'Jam'             },
      { key: 'open_limp',    label: 'Limp'            },
      { key: 'open_mixed',   label: 'Mixed'           },
      { key: 'sb_vs_btn_ai', label: 'Call vs BTN Jam' },
    ],
    secondary: {
      open_jam:     null,
      open_limp:    SB_LIMP_RESPONSE_TYPES_7_9,  // BB Jam | BB Large Raise | BB Small Raise
      open_mixed:   null,
      sb_vs_btn_ai: null,
    },
  },

  '3way_SB_7': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: 3,
    primaryActions: [
      { key: 'open_jam',     label: 'Jam'             },
      { key: 'open_limp',    label: 'Limp'            },
      { key: 'open_mixed',   label: 'Mixed'           },
      { key: 'sb_vs_btn_ai', label: 'Call vs BTN Jam' },
    ],
    secondary: {
      open_jam:     null,
      open_limp:    SB_LIMP_RESPONSE_TYPES_7_9,  // BB Jam | BB Large Raise | BB Small Raise
      open_mixed:   null,
      sb_vs_btn_ai: null,
    },
  },

  // Jam only + Call vs BTN Jam (no limp/mixed at short stacks)
  '3way_SB_6': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [
      { key: 'open_jam',     label: 'Jam'             },
      { key: 'sb_vs_btn_ai', label: 'Call vs BTN Jam' },
    ],
    secondary: { open_jam: null, sb_vs_btn_ai: null },
  },

  '3way_SB_3': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [
      { key: 'open_jam',     label: 'Jam'             },
      { key: 'sb_vs_btn_ai', label: 'Call vs BTN Jam' },
    ],
    secondary: { open_jam: null, sb_vs_btn_ai: null },
  },

  // ── 3-Way BB ──────────────────────────────────────────────────────────────
  // bb_vs_sb_call (7,9,10,12,15bb): BB option when SB limps — Option Type secondary
  // bb_vs_btn_open (10,15bb): BB faces BTN open
  // bb_vs_btn_ai   (4,5,7,9,12bb): BB faces BTN jam
  // bb_vs_sb_open  (7,9,12,15bb): BB faces SB open/raise
  // bb_vs_btn_open_vs_sb_ai (10,12,15bb): BTN opens, SB jams, BB decides
  // (renamed from bb_vs_sb_ai_vs_btn_open — see rename note at top of file)

  '3way_BB_15': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: 3,
    primaryActions: [
      { key: 'bb_vs_btn_ai',             label: 'BTN Jam'            },  // 15bb CSV exists ✓
      { key: 'bb_vs_btn_open',           label: 'BTN Raise'          },
      { key: 'bb_vs_sb_ai',              label: 'SB Jam'             },  // 15bb CSV exists ✓
      { key: 'bb_vs_sb_open',           label: 'SB Raise'           },
      { key: 'bb_vs_sb_call',           label: 'SB Limp'            },
      { key: 'bb_vs_btn_open_vs_sb_ai', label: 'SB Jam vs BTN Open' },  // 15bb CSV exists ✓
    ],
    secondary: {
      bb_vs_btn_ai:            null,              // call only
      bb_vs_btn_open:          yourResponse(),
      bb_vs_sb_ai:              null,              // call only
      bb_vs_sb_open:           yourResponse(),
      bb_vs_sb_call:           yourResponse(BB_OPTION_2SIZES_3WAY_12_15),
      bb_vs_btn_open_vs_sb_ai: null,              // call only
    },
  },

  '3way_BB_12': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: 3,
    primaryActions: [
      { key: 'bb_vs_btn_ai',             label: 'BTN Jam'            },  // 12bb CSV exists ✓
      { key: 'bb_vs_btn_open',           label: 'BTN Raise'          },  // 12bb CSV exists ✓
      { key: 'bb_vs_sb_ai',              label: 'SB Jam'             },  // 12bb CSV exists ✓
      { key: 'bb_vs_sb_open',           label: 'SB Raise'           },  // 12bb CSV exists ✓
      { key: 'bb_vs_sb_call',           label: 'SB Limp'            },  // 12bb CSV exists ✓
      { key: 'bb_vs_btn_open_vs_sb_ai', label: 'SB Jam vs BTN Open' },  // 12bb CSV exists ✓
    ],
    secondary: {
      bb_vs_btn_ai:            null,              // call only
      bb_vs_btn_open:          yourResponse(),
      bb_vs_sb_ai:              null,              // call only
      bb_vs_sb_open:           yourResponse(),
      bb_vs_sb_call:           yourResponse(BB_OPTION_2SIZES_3WAY_12_15),
      bb_vs_btn_open_vs_sb_ai: null,              // call only
    },
  },

  '3way_BB_10': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: 3,
    primaryActions: [
      { key: 'bb_vs_btn_ai',             label: 'BTN Jam'            },  // 10bb CSV exists ✓
      { key: 'bb_vs_btn_open',           label: 'BTN Raise'          },  // 10bb CSV exists ✓
      { key: 'bb_vs_sb_ai',              label: 'SB Jam'             },  // 10bb CSV exists ✓
      { key: 'bb_vs_sb_open',           label: 'SB Raise'           },  // 10bb CSV exists ✓
      { key: 'bb_vs_sb_call',           label: 'SB Limp'            },  // 10bb CSV exists ✓
      { key: 'bb_vs_btn_open_vs_sb_ai', label: 'SB Jam vs BTN Open' },  // 10bb CSV exists ✓
    ],
    secondary: {
      bb_vs_btn_ai:            null,
      bb_vs_btn_open:          yourResponse(),
      bb_vs_sb_ai:              null,
      bb_vs_sb_open:           yourResponse(),
      bb_vs_sb_call:           yourResponse(BB_OPTION_2SIZES),
      bb_vs_btn_open_vs_sb_ai: null,
    },
  },

  '3way_BB_9': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: 2,
    primaryActions: [
      { key: 'bb_vs_btn_ai',  label: 'BTN Jam' },
      { key: 'bb_vs_sb_ai',  label: 'SB Jam'  },  // CSV exists ✓
      { key: 'bb_vs_sb_call', label: 'SB Limp' },
    ],
    secondary: {
      bb_vs_btn_ai:  null,              // call only
      bb_vs_sb_ai:  null,              // call only
      bb_vs_sb_call: yourResponse(BB_OPTION_2SIZES_3WAY_7_9),
    },
  },

  '3way_BB_7': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: 2,
    primaryActions: [
      { key: 'bb_vs_btn_ai',  label: 'BTN Jam' },
      { key: 'bb_vs_sb_ai',  label: 'SB Jam'  },  // CSV exists ✓
      { key: 'bb_vs_sb_call', label: 'SB Limp' },
    ],
    secondary: {
      bb_vs_btn_ai:  null,              // call only
      bb_vs_sb_ai:  null,              // call only
      // 7bb only has one raise size vs a limp (no large/small split) — see BB_OPTION_1SIZE
      bb_vs_sb_call: yourResponse(BB_OPTION_1SIZE),
    },
  },

  // 5bb: BTN Jam, SB Jam
  '3way_BB_5': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: null,
    primaryActions: [
      { key: 'bb_vs_btn_ai', label: 'BTN Jam' },
      { key: 'bb_vs_sb_ai', label: 'SB Jam'  },  // CSV exists ✓
    ],
    secondary: {
      bb_vs_btn_ai: null,
      bb_vs_sb_ai:  null,              // call only
    },
  },

  '3way_BB_4': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: null,
    primaryActions: [
      { key: 'bb_vs_btn_ai', label: 'BTN Jam' },
      { key: 'bb_vs_sb_ai', label: 'SB Jam'  },  // CSV exists ✓ (was missing from this rule — see bb_vs_sb_ai_4bb.csv)
    ],
    secondary: {
      bb_vs_btn_ai: null,
      bb_vs_sb_ai:  null,              // call only
    },
  },

  // ── Heads Up SB ───────────────────────────────────────────────────────────
  // sb_open (all stacks; raise+allin+limp at 15bb; limp+raise at 25bb; allin+limp at 6-12bb; allin at 2,3bb)
  // sb_vs_sb_raise_vs_bb_3b_small (15,25bb): SB faces BB 3bet small → allin+call
  // sb_vs_sb_raise_vs_bb_3b_large (25bb):    SB faces BB 3bet large → allin+call
  // sb_vs_sb_open_bb_ai (15,25bb): SB faces BB jam → call only
  //
  // BB Response Types secondary changes the loaded CSV when a BB action is selected.

  'hu_SB_25': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [
      { key: 'open_raise', label: 'Raise' },
      { key: 'open_limp',  label: 'Limp'  },
      { key: 'open_mixed', label: 'Mixed' },
    ],
    secondary: {
      open_raise: bbResponseTypes([HU_SB_BB_JAM, HU_SB_3B_LARGE, HU_SB_3B_SMALL]),
      open_limp:  bbLimpResponseTypes([HU_SB_LIMP_BB_JAM, HU_SB_LIMP_RAISE_LARGE, HU_SB_LIMP_RAISE_SMALL]),
      open_mixed: null,
    },
  },

  'hu_SB_15': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [
      { key: 'open_jam',   label: 'Jam'   },
      { key: 'open_raise', label: 'Raise' },
      { key: 'open_limp',  label: 'Limp'  },
      { key: 'open_mixed', label: 'Mixed' },
    ],
    secondary: {
      open_raise: bbResponseTypes([HU_SB_BB_JAM, HU_SB_3B]),  // Jam first; single "3bet" (no Large/Small at 15bb)
      open_jam:   null,
      open_limp:  bbLimpResponseTypes([HU_SB_LIMP_BB_JAM, HU_SB_LIMP_RAISE_LARGE, HU_SB_LIMP_RAISE_SMALL]),
      open_mixed: null,
    },
  },

  'hu_SB_12': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [
      { key: 'open_jam',   label: 'Jam'   },
      { key: 'open_limp',  label: 'Limp'  },
      { key: 'open_mixed', label: 'Mixed' },
    ],
    secondary: {
      open_jam:   null,
      open_limp:  bbLimpResponseTypes([HU_SB_LIMP_BB_JAM, HU_SB_LIMP_RAISE_LARGE, HU_SB_LIMP_RAISE_SMALL]),
      open_mixed: null,
    },
  },

  'hu_SB_10': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [
      { key: 'open_jam',   label: 'Jam'   },
      { key: 'open_limp',  label: 'Limp'  },
      { key: 'open_mixed', label: 'Mixed' },
    ],
    secondary: {
      open_jam:   null,
      open_limp:  bbLimpResponseTypes([HU_SB_LIMP_BB_JAM, HU_SB_LIMP_RAISE_LARGE, HU_SB_LIMP_RAISE_SMALL]),
      open_mixed: null,
    },
  },

  'hu_SB_8': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [
      { key: 'open_jam',   label: 'Jam'   },
      { key: 'open_limp',  label: 'Limp'  },
      { key: 'open_mixed', label: 'Mixed' },
    ],
    secondary: {
      open_jam:   null,
      open_limp:  bbLimpResponseTypes([HU_SB_LIMP_BB_JAM, HU_SB_LIMP_RAISE_LARGE, HU_SB_LIMP_RAISE_SMALL]),
      open_mixed: null,
    },
  },

  'hu_SB_6': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [
      { key: 'open_jam',   label: 'Jam'   },
      { key: 'open_limp',  label: 'Limp'  },
      { key: 'open_mixed', label: 'Mixed' },
    ],
    secondary: {
      open_jam:   null,
      open_limp:  bbLimpResponseTypes([HU_SB_LIMP_BB_JAM, HU_SB_LIMP_RAISE_LARGE, HU_SB_LIMP_RAISE_SMALL]),
      open_mixed: null,
    },
  },
  'hu_SB_3': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [{ key: 'open_jam', label: 'Jam' }],
    secondary: { open_jam: null },
  },
  'hu_SB_2': {
    primaryLabel: 'Choose Your Opening Action',
    primaryRowBreak: null,
    primaryActions: [{ key: 'open_jam', label: 'Jam' }],
    secondary: { open_jam: null },
  },

  // ── Heads Up BB ───────────────────────────────────────────────────────────
  // bb_vs_sb_call (6,8,10,15,25bb): BB faces SB limp
  // bb_vs_sb_open (15,25bb):        BB faces SB raise
  // bb_vs_sb_ai   (3,4,6,8,10,15bb): BB faces SB jam
  // bb_vs_4bet_jam / bb_vs_3bet_jam: stub — CSV pending

  'hu_BB_25': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: null,
    primaryActions: [
      { key: 'bb_vs_sb_open',   label: 'Raise'    },
      { key: 'bb_vs_sb_call',   label: 'Limp'     },
    ],
    secondary: {
      bb_vs_sb_open:  yourResponse(BB_OPTION_RAISE),
      bb_vs_sb_call:  yourResponse(BB_OPTION_2SIZES_HU),
    },
  },

  'hu_BB_15': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: null,
    primaryActions: [
      { key: 'bb_vs_sb_ai',    label: 'Jam'      },
      { key: 'bb_vs_sb_open',  label: 'Raise'    },
      { key: 'bb_vs_sb_call',  label: 'Limp'     },
    ],
    secondary: {
      bb_vs_sb_ai:    null,                        // call only
      bb_vs_sb_open:  yourResponse(),              // single raise size → Jam/Call/Mixed
      bb_vs_sb_call:  yourResponse(BB_OPTION_2SIZES_HU),
    },
  },

  'hu_BB_10': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: null,
    primaryActions: [
      { key: 'bb_vs_sb_ai',    label: 'Jam'      },
      { key: 'bb_vs_sb_call',  label: 'Limp'     },
    ],
    secondary: {
      bb_vs_sb_ai:    null,
      bb_vs_sb_call:  yourResponse(BB_OPTION_2SIZES_HU),
    },
  },

  'hu_BB_8': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: null,
    primaryActions: [
      { key: 'bb_vs_sb_ai',    label: 'Jam'      },
      { key: 'bb_vs_sb_call',  label: 'Limp'     },
    ],
    secondary: {
      bb_vs_sb_ai:    null,
      bb_vs_sb_call:  yourResponse(BB_OPTION_2SIZES_HU),
    },
  },

  'hu_BB_6': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: null,
    primaryActions: [
      { key: 'bb_vs_sb_ai',    label: 'Jam'      },
      { key: 'bb_vs_sb_call',  label: 'Limp'     },
    ],
    secondary: {
      bb_vs_sb_ai:    null,
      bb_vs_sb_call:  yourResponse(BB_OPTION_2SIZES_HU),
    },
  },

  // Jam-only stacks
  'hu_BB_4': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: null,
    primaryActions: [{ key: 'bb_vs_sb_ai', label: 'Jam' }],
    secondary: { bb_vs_sb_ai: null },
  },
  'hu_BB_3': {
    primaryLabel: "What's Villain's Action?",
    primaryRowBreak: null,
    primaryActions: [{ key: 'bb_vs_sb_ai', label: 'Jam' }],
    secondary: { bb_vs_sb_ai: null },
  },

};

// ─── Secondary CSV override helpers ──────────────────────────────────────────

/**
 * Returns true when the selected secondary (or tertiary) option switches the
 * loaded CSV — meaning the quiz is a *reaction* scenario, not the hero's
 * opening action.  Examples: SB Limp → BB Jam, SB Raise → BB Jam, etc.
 */
const secondaryOverridesCsv = (rule, primaryAction, secondaryAction, tertiaryAction) => {
  const secCfg = rule?.secondary?.[primaryAction];
  const secOpt = secCfg?.options?.find(o => o.key === secondaryAction);
  if (secOpt?.csvDir) return true;
  const terOpt = secOpt?.tertiary?.options?.find(o => o.key === tertiaryAction);
  return !!terOpt?.csvDir;
};

/**
 * Derive villainPos / villainAction from a csvDir string.
 * Uses the LAST position token (vs_btn / vs_sb / vs_bb) so multi-position dirs
 * like 'sb_vs_sb_call_vs_bb_raise' resolve to 'BB', not 'SB'.
 * Villain action is 'jam' when the dir contains '_ai' or ends with '_jam'.
 */
const villainFromCsvDir = (dir) => {
  // Special case: _bb_ai suffix means BB jammed over hero's (SB's) open raise.
  // The dir name starts with 'sb_vs_sb_open_…' so a naive vs_* scan would
  // incorrectly pick up SB as the villain — detect BB jam explicitly first.
  if (dir.includes('_bb_ai')) {
    return { villainPos: 'BB', villainAction: 'jam' };
  }

  // Detect villain action before scanning for position so we can detect 3bets.
  let villainAction = 'open';
  if (dir.includes('_ai') || dir.endsWith('_jam')) {
    villainAction = 'jam';
  } else if (dir.includes('_3b_large')) {
    villainAction = '3bet large';
  } else if (dir.includes('_3b_small') || dir.includes('_3b')) {
    villainAction = '3bet small';
  } else if (dir.includes('_raise_large')) {
    villainAction = 'raise large';
  } else if (dir.includes('_raise_small')) {
    villainAction = 'raise small';
  }

  let villainPos = null;
  for (const pos of ['BTN', 'SB', 'BB']) {
    if (dir.toLowerCase().includes(`vs_${pos.toLowerCase()}`)) villainPos = pos;
  }
  return { villainPos, villainAction };
};

// ─── Accessor ─────────────────────────────────────────────────────────────────

/** Returns the rule entry for a scenario, or null if unlisted. */
export const getActionConfig = (format, position, stack) =>
  BLAST_RANGE_RULES[`${format}_${position}_${stack}`] ?? null;

// ─── Auto-select ──────────────────────────────────────────────────────────────

/**
 * If the rule has exactly ONE primary action before the row break (or no row
 * break and one total action), return that action key for auto-selection.
 * Returns null when the user must choose.
 */
export const getAutoConfig = (rule) => {
  if (!rule) return null;
  const { primaryActions, primaryRowBreak } = rule;
  const openingActions = primaryRowBreak != null
    ? primaryActions.slice(0, primaryRowBreak)
    : primaryActions;
  if (openingActions.length === 1) {
    return { primaryAction: openingActions[0].key };
  }
  return null;
};

// ─── CSV directory derivation ─────────────────────────────────────────────────

/**
 * Returns the CSV directory name for a given config selection.
 *
 * Logic:
 *   1. If the selected tertiary option has its own csvDir, use that (e.g. BB raise → SB jam).
 *   2. If the selected secondary option has its own csvDir, use that (HU SB BB Response).
 *   3. If the primaryAction starts with 'open_', use '{pos_lower}_open'.
 *   4. Otherwise the primaryAction key IS the directory name.
 */
export const getCsvDir = (format, position, primaryAction, secondaryAction, rule, tertiaryAction) => {
  // Check whether the selected tertiary option overrides the CSV
  if (tertiaryAction && secondaryAction && rule) {
    const secCfg = rule.secondary?.[primaryAction];
    const secOpt = secCfg?.options?.find(o => o.key === secondaryAction);
    const terOpt = secOpt?.tertiary?.options?.find(o => o.key === tertiaryAction);
    if (terOpt?.csvDir) return terOpt.csvDir;
  }

  // Check whether the selected secondary option overrides the CSV
  if (secondaryAction && rule) {
    const secCfg = rule.secondary?.[primaryAction];
    if (secCfg) {
      const secOpt = secCfg.options.find(o => o.key === secondaryAction);
      if (secOpt?.csvDir) return secOpt.csvDir;
    }
  }

  // Opening scenarios
  if (primaryAction && primaryAction.startsWith('open_')) {
    return `${position.toLowerCase()}_open`;
  }

  // Reaction scenarios: key IS the directory name
  return primaryAction;
};

// ─── Filter mode derivation ───────────────────────────────────────────────────

/** Maps primary action key → RangeMatrix filterMode (when no secondary selected). */
const PRIMARY_FILTER = {
  open_raise: 'raise',
  open_jam:   'allin',
  open_limp:  'limp',
  open_mixed: null,
};

/**
 * Maps secondary / tertiary filter key → RangeMatrix filterMode.
 * Used for BB Option Type and Jam/Call/Mixed Response selectors.
 */
const SECONDARY_FILTER = {
  jam:         'allin',
  call:        'call',
  mixed:       null,
  check:       'check',
  raise_large: 'raise-large',
  raise_small: 'raise-small',
  raise:       'raise-all',
};

/**
 * Derives the RangeMatrix filterMode from the three-level selection.
 *
 * Priority: tertiary (filter only) > secondary (filter only) > primary
 *
 * If secondary is a CSV-changer key (like 'sb_vs_sb_raise_vs_bb_3b_small'), it doesn't
 * itself set a filter — the tertiary does. Without a tertiary, filterMode = null
 * (show everything in the newly loaded CSV).
 *
 * Tertiary keys come in two flavors: real filter keys (e.g. 'jam'/'call'/'mixed'
 * from yourResponse()) and CSV-changer keys (e.g. 'sb_jam' from the "SB
 * Response" tertiary used when SB jams back over BB's raise-large/raise-small).
 * A CSV-changer tertiary doesn't set its own filter, so it must fall through to
 * the secondary's filter instead of nulling it out — otherwise a "BB Raise
 * Large" quiz that also has SB jamming back loses its 'raise-large' filterMode,
 * which breaks the answer screen's per-stack circle labels/colors (they'd fall
 * back to generic "Raise" instead of "Large Raise", showing the wrong color).
 */
export const getFilterMode = (primaryAction, secondaryAction, tertiaryAction) => {
  // Tertiary wins when present AND it's a recognized filter key.
  if (tertiaryAction != null && Object.prototype.hasOwnProperty.call(SECONDARY_FILTER, tertiaryAction)) {
    return SECONDARY_FILTER[tertiaryAction];
  }

  if (secondaryAction != null) {
    // Is this a plain filter key?
    if (Object.prototype.hasOwnProperty.call(SECONDARY_FILTER, secondaryAction)) {
      return SECONDARY_FILTER[secondaryAction];
    }
    // It's a CSV-change key (HU SB BB Response) — no filter yet, show whole new CSV
    return null;
  }

  return PRIMARY_FILTER[primaryAction] ?? null;
};

// ─── Range file path ──────────────────────────────────────────────────────────

/**
 * Resolves the CSV file path for a config object.
 *
 * Supports both new config shape (config.csvDir present) and the legacy shape
 * (config.action / config.villainPos / config.villainAction).
 */
export const getRangeFilePath = (config) => {
  const { format, position, stack } = config;
  const base       = 'database/blast/preflop';
  const formatPath = format === '3way' ? '3_way' : 'hu';
  const stackBB    = `${stack}bb`;

  // ── New config shape ───────────────────────────────────────────────────────
  if (config.csvDir) {
    return `${base}/${formatPath}/${config.csvDir}/${config.csvDir}_${stackBB}.csv`;
  }

  // ── Legacy config shape (backward compat) ─────────────────────────────────
  const villainPos    = (config.villainPos    ?? '').toUpperCase();
  const villainAction = (config.villainAction ?? '').toLowerCase();
  const action        = config.action ?? '';

  if (format === '3way') {
    if (position === 'BTN') {
      if (action === 'open')
        return `${base}/3_way/btn_open/btn_open_${stackBB}.csv`;
      if (action === 'call' && villainPos === 'SB' && villainAction === 'jam')
        return `${base}/3_way/btn_vs_btn_open_vs_sb_ai/btn_vs_btn_open_vs_sb_ai_${stackBB}.csv`;
    }
    if (position === 'SB') {
      if (action === 'open')
        return `${base}/3_way/sb_open/sb_open_${stackBB}.csv`;
      if (action === 'jam')
        return `${base}/3_way/sb_vs_btn_open/sb_vs_btn_open_${stackBB}.csv`;
      if (action === 'call') {
        if (villainPos === 'BTN') {
          if (villainAction === 'open') return `${base}/3_way/sb_vs_btn_open/sb_vs_btn_open_${stackBB}.csv`;
          if (villainAction === 'jam')  return `${base}/3_way/sb_vs_btn_ai/sb_vs_btn_ai_${stackBB}.csv`;
        }
        if (villainPos === 'BB' && villainAction === 'jam')
          // sb_vs_bb_ai no longer exists — this action order doesn't occur
          // (see "3-way scenario renames" note at top of file). Left as a
          // dead branch rather than removed, in case a legacy caller still
          // reaches it; it will 404 until/unless this situation returns.
          return `${base}/3_way/sb_vs_bb_ai/sb_vs_bb_ai_${stackBB}.csv`;
      }
    }
    if (position === 'BB') {
      if (action === 'check' || action === 'raise')
        return `${base}/3_way/bb_vs_sb_call/bb_vs_sb_call_${stackBB}.csv`;
      if (action === 'call') {
        if (villainPos === 'BTN') {
          if (villainAction === 'open') return `${base}/3_way/bb_vs_btn_open/bb_vs_btn_open_${stackBB}.csv`;
          if (villainAction === 'jam')  return `${base}/3_way/bb_vs_btn_ai/bb_vs_btn_ai_${stackBB}.csv`;
        }
        if (villainPos === 'SB' && villainAction === 'open')
          return `${base}/3_way/bb_vs_sb_open/bb_vs_sb_open_${stackBB}.csv`;
      }
    }
  }

  if (format === 'hu') {
    if (position === 'SB') {
      if (action === 'open')
        return `${base}/hu/sb_open/sb_open_${stackBB}.csv`;
      if (action === 'call' && villainPos === 'BB') {
        if (villainAction === '3bet small') return `${base}/hu/sb_vs_sb_raise_vs_bb_3b_small/sb_vs_sb_raise_vs_bb_3b_small_${stackBB}.csv`;
        if (villainAction === '3bet large') return `${base}/hu/sb_vs_sb_raise_vs_bb_3b_large/sb_vs_sb_raise_vs_bb_3b_large_${stackBB}.csv`;
        if (villainAction === 'jam')        return `${base}/hu/sb_vs_sb_open_bb_ai/sb_vs_sb_open_bb_ai_${stackBB}.csv`;
      }
    }
    if (position === 'BB') {
      if (action === 'check' || action === 'raise')
        return `${base}/hu/bb_vs_sb_call/bb_vs_sb_call_${stackBB}.csv`;
      if (action === 'call' && villainPos === 'SB') {
        if (villainAction === 'open') return `${base}/hu/bb_vs_sb_open/bb_vs_sb_open_${stackBB}.csv`;
        if (villainAction === 'jam')  return `${base}/hu/bb_vs_sb_ai/bb_vs_sb_ai_${stackBB}.csv`;
      }
    }
  }

  console.error('getRangeFilePath: no matching path for config', config);
  return null;
};

// ─── Build complete config ────────────────────────────────────────────────────

/**
 * Builds the full config object that gets passed to BlastRangeScreen.
 *
 * Produces both the new fields (primaryAction, csvDir, filterMode) AND the
 * legacy fields (action, subtype, villainPos, villainAction) so that existing
 * consumers keep working without changes.
 */
export const buildBlastConfig = (format, position, stack, primaryAction, secondaryAction, tertiaryAction) => {
  const rule       = getActionConfig(format, position, stack);
  const csvDir     = getCsvDir(format, position, primaryAction, secondaryAction, rule, tertiaryAction);
  const filterMode = getFilterMode(primaryAction, secondaryAction, tertiaryAction);

  // When a secondary/tertiary option switches the CSV it means we're in a
  // reaction scenario, not a pure opening range.
  const isReaction = secondaryOverridesCsv(rule, primaryAction, secondaryAction, tertiaryAction);

  // ── Derive legacy fields for backward compat ─────────────────────────────
  let action, subtype, villainPos, villainAction;

  if (primaryAction.startsWith('open_') && !isReaction) {
    action  = 'open';
    subtype = { open_raise: 'raise', open_jam: 'jam', open_limp: 'limp', open_mixed: 'all' }[primaryAction] ?? null;
  } else if (primaryAction === 'bb_vs_sb_call') {
    // BB Option (vs SB limp) — map secondary to old action/subtype
    if (secondaryAction === 'check')       { action = 'check'; subtype = 'check'; }
    else if (secondaryAction === 'jam')    { action = 'raise'; subtype = 'allin'; }
    else if (secondaryAction === 'raise' ||
             secondaryAction === 'raise_large' ||
             secondaryAction === 'raise_small') { action = 'raise'; subtype = secondaryAction; }
    else                                   { action = 'raise'; subtype = 'all'; }
  } else {
    // Reaction / call scenario (explicit, or secondary/tertiary CSV override)
    ({ villainPos, villainAction } = villainFromCsvDir(csvDir ?? ''));
    if (primaryAction === 'open_jam') {
      // SB jammed and villain raised back — hero is deciding whether to jam (call is all-in already)
      action  = 'jam';
      subtype = 'allin';
    } else if (filterMode === 'allin') {
      // User selected "Jam" as their response type — this is a jam-frequency quiz, not a call quiz
      action  = 'jam';
      subtype = filterMode;
    } else {
      action  = 'call';
      subtype = filterMode;
    }
  }

  return {
    format,
    position,
    stack,

    // New fields
    primaryAction,
    secondaryAction: secondaryAction ?? null,
    tertiaryAction:  tertiaryAction  ?? null,
    filterMode,
    csvDir,

    // Legacy fields
    action,
    subtype:      filterMode ?? subtype,  // BlastRangeScreen reads config.subtype for filterMode
    villainPos,
    villainAction,

    // Game config
    quizType:     'BLAST',
    totalHands:   10,
    heroPosition: position,
  };
};

// ─── Format title ─────────────────────────────────────────────────────────────

/**
 * Converts a secondary/tertiary option label into a "Villain Action" phrase.
 *
 * Handles labels that already carry a position prefix ("BB Jam", "BTN Raise")
 * and bare labels that assume the villain is BB ("Jam", "3bet Large").
 *
 * Labels are already size-first ("Large Raise", "Small Raise") at the source
 * (BLAST_RANGE_RULES option labels), so no reordering is needed here.
 */
const toVillainLabel = (label) => {
  if (!label) return '';
  const positionPrefixes = ['BB ', 'SB ', 'BTN ', 'CO ', 'HJ ', 'LJ '];
  let prefix = '';
  let action = label;
  for (const p of positionPrefixes) {
    if (label.startsWith(p)) { prefix = p.trimEnd(); action = label.slice(p.length); break; }
  }
  if (!prefix) prefix = 'BB';
  return `${prefix} ${action}`;
};

/**
 * Returns a human-readable title for the range being studied.
 * Works with both new config shape (primaryAction) and legacy shape.
 *
 * Conventions:
 *  - No trailing "Range" word — subtext already explains it's a range.
 *  - No format prefix ("HU" / "3 Way") — that belongs in the screen's page header.
 *  - Two sequential hero actions are joined with a hyphen: Limp-jam, Limp-call, Raise-call.
 *    Both words are lower-case after the hyphen for consistency.
 */
export const formatBlastTitle = (config) => {
  if (!config) return 'Range';

  const { format, position, primaryAction, secondaryAction, tertiaryAction } = config;
  const pos = position ?? '';

  // ── New config shape ──────────────────────────────────────────────────────
  if (primaryAction) {

    // ── SB / hero opening scenarios ─────────────────────────────────────────

    if (primaryAction === 'open_raise') {
      if (secondaryAction && secondaryAction !== 'mixed') {
        const rule = BLAST_RANGE_RULES[`${format}_${position}_${config.stack}`];
        const secCfg = rule?.secondary?.open_raise;
        const secOpt = secCfg?.options.find(o => o.key === secondaryAction);
        const secLabel = secOpt?.label ?? secondaryAction;
        const villainLabel = toVillainLabel(secLabel);

        if (tertiaryAction && tertiaryAction !== 'mixed') {
          // Hero raised → villain acted → hero responds (two hero actions)
          const heroResponse = tertiaryAction === 'jam' ? 'jam' : 'call';
          return `${pos} Raise-${heroResponse} vs ${villainLabel}`;
        }
        // BB jammed (call-only range) — no tertiary needed
        if (secondaryAction === 'sb_vs_sb_open_bb_ai') {
          return `${pos} Raise-call vs ${villainLabel}`;
        }
        // Full range vs villain 3bet (no action filter selected yet)
        return `${pos} Raise vs ${villainLabel}`;
      }
      return `${pos} Open-Raising`;
    }

    if (primaryAction === 'open_jam') {
      if (secondaryAction && secondaryAction !== 'mixed') {
        // Hero is jamming OVER a villain open (csvDir switched via
        // secondary.open_jam — e.g. sb_vs_btn_open at 3way_SB_12/15bb), not
        // opening themselves. "Open-Jamming" would be misleading here; match
        // the in-game table label convention: "SB Jam vs BTN Raise".
        const rule = BLAST_RANGE_RULES[`${format}_${position}_${config.stack}`];
        const secCfg = rule?.secondary?.open_jam;
        const secOpt = secCfg?.options.find(o => o.key === secondaryAction);
        const secLabel = secOpt?.label ?? secondaryAction;
        const villainLabel = toVillainLabel(secLabel);
        return `${pos} Jam vs ${villainLabel}`;
      }
      return `${pos} Open-Jamming`;
    }

    if (primaryAction === 'open_limp') {
      if (secondaryAction && secondaryAction !== 'mixed') {
        const rule = BLAST_RANGE_RULES[`${format}_${position}_${config.stack}`];
        const secCfg = rule?.secondary?.open_limp;
        const secOpt = secCfg?.options.find(o => o.key === secondaryAction);
        const secLabel = secOpt?.label ?? secondaryAction;
        const villainLabel = toVillainLabel(secLabel);

        if (tertiaryAction && tertiaryAction !== 'mixed') {
          // Hero limped → villain raised → hero responds (two hero actions)
          const heroResponse = tertiaryAction === 'jam' ? 'jam' : 'call';
          return `${pos} Limp-${heroResponse} vs ${villainLabel}`;
        }
        // BB jammed over limp — SB must call or fold (call range, no tertiary needed)
        if (secondaryAction === 'sb_vs_sb_limp_vs_bb_jam') {
          return `${pos} Limp-call vs ${villainLabel}`;
        }
        // Full range vs villain raise (no action filter selected yet)
        return `${villainLabel} vs ${pos} Limp`;
      }
      return `${pos} Limp`;
    }

    if (primaryAction === 'open_mixed') return `${pos} Open`;

    // ── BB Option (facing SB limp) ───────────────────────────────────────────

    if (primaryAction === 'bb_vs_sb_call') {
      const optionLabels = { jam: 'Jam', call: 'Call', check: 'Check', raise: 'Raise',
                             raise_large: 'Large Raise', raise_small: 'Small Raise' };
      if (!secondaryAction || secondaryAction === 'mixed') return `${pos} Option`;
      const secLabel = optionLabels[secondaryAction] ?? secondaryAction;
      if (tertiaryAction && tertiaryAction !== 'mixed') {
        // BB raised → SB jammed back → BB responds (two BB actions: Large Raise-call vs SB Jam)
        return `${pos} ${secLabel}-call vs SB Jam`;
      }
      return `${pos} Option-${secLabel}`;
    }

    // ── Reaction / facing-action scenarios ───────────────────────────────────

    const rule = BLAST_RANGE_RULES[`${format}_${position}_${config.stack}`];
    const primOpt = rule?.primaryActions.find(a => a.key === primaryAction);
    const primLabel = primOpt?.label ?? primaryAction;

    // ── BB facing-action scenarios (bb_vs_* keys) ──────────────────────────
    // HU: villain is always SB and labels are bare ("Jam", "Raise", "Limp").
    // 3-way: labels already include position ("BTN Jam", "SB Raise", etc.).
    // Goal: "BB Call vs SB Jam", "BB vs SB Raise", "BB Jam vs BTN Raise", etc.
    if (position === 'BB' && primaryAction.startsWith('bb_vs_')) {
      const huBareMap = { 'Jam': 'SB Jam', 'Raise': 'SB Raise', 'Limp': 'SB Limp' };
      const villainStr = (format === 'hu' && huBareMap[primLabel]) ? huBareMap[primLabel] : primLabel;

      // All-in jam facing — BB can only call, make that explicit.
      if (primaryAction.includes('_ai')) {
        return `${pos} Call vs ${villainStr}`;
      }

      // BB has options (raise/call/check). Reflect hero's response when selected.
      const heroActionMap = {
        jam: 'Jam', call: 'Call', check: 'Check',
        raise_large: 'Large Raise', raise_small: 'Small Raise', raise: 'Raise',
      };
      const heroAction = (secondaryAction && secondaryAction !== 'mixed')
        ? (heroActionMap[secondaryAction] ?? null)
        : null;

      if (heroAction) {
        return `${pos} ${heroAction} vs ${villainStr}`;
      }
      return `${pos} vs ${villainStr}`;
    }

    // ── Other reaction scenarios (e.g. SB Call vs BTN Jam) ─────────────────
    if (secondaryAction && secondaryAction !== 'mixed') {
      const terLabel = tertiaryAction && tertiaryAction !== 'mixed'
        ? ` (${tertiaryAction.charAt(0).toUpperCase() + tertiaryAction.slice(1)})`
        : '';
      return `${pos} ${primLabel}${terLabel}`;
    }
    return `${pos} ${primLabel}`;
  }

  // ── Legacy config shape ───────────────────────────────────────────────────
  const { action, subtype, villainPos: vPos, villainAction: vAct } = config;

  if (position === 'BB' && (action === 'check' || action === 'raise')) {
    if (!subtype || subtype === 'all')      return `BB Full Option`;
    if (subtype === 'check')                return `BB Option-Check`;
    if (subtype === 'allin')                return `BB Option-Jam`;
    if (subtype?.startsWith('raise_')) {
      const size = subtype.replace('raise_', '');
      return `BB Option-Raise ${size}`;
    }
    return `BB Option`;
  }
  if (action === 'open') {
    const map = { raise: 'Raising', jam: 'Jamming', limp: 'Limping', call: 'Limping' };
    const lbl = subtype && subtype !== 'all' ? `-${map[subtype] ?? subtype}` : '';
    return `${pos} Open${lbl}`;
  }
  if (action === 'jam')  return `${pos} Jam vs BTN Open`;
  if (action === 'call') {
    const vLabel = vPos && vAct
      ? ` vs ${vPos} ${vAct.replace(/\b\w/g, c => c.toUpperCase())}`
      : '';
    return `${pos} Call${vLabel}`;
  }
  return `${pos} ${action ?? ''}`;
};

// ─── Page header title ───────────────────────────────────────────────────────

/**
 * Returns the main page header title for a Blast screen.
 * e.g. "Blast - Heads Up" or "Blast - 3 Way"
 */
export const getBlastPageTitle = (format) => {
  if (format === 'hu')   return 'Blast - Heads Up';
  if (format === '3way') return 'Blast - 3 Way';
  return 'Blast Tournament';
};

// ─── Fixed Hands stacks ───────────────────────────────────────────────────────

/**
 * All valid big-blind levels for each format + position combo.
 * Keys use the internal format names ('3way' | 'hu').
 * Used by Fixed Hands mode to determine which stack depths to cycle through.
 */
export const STACKS_BY_FORMAT = {
  '3way': {
    BTN: [15, 12, 9, 6, 4],
    SB:  [15, 12, 9, 7, 6, 3],
    BB:  [15, 12, 10, 9, 7, 5, 4],
  },
  'hu': {
    SB: [25, 15, 12, 10, 8, 6, 3, 2],
    BB: [25, 15, 10, 8, 6, 4, 3],
  },
};

// ─── Fixed Hands config ───────────────────────────────────────────────────────

/**
 * Builds the full config object for Fixed Hands mode.
 *
 * Like buildBlastConfig but without a specific stack. The config includes:
 *   isFixedHands: true
 *   availableStacks: all valid stacks for this format+position
 *
 * The reference rule is derived from the largest available stack so that
 * secondary / tertiary CSV lookups work correctly.
 */
/**
 * Stacks where the base opening CSV actually contains raise actions.
 * Used to prevent open_raise Fixed Hands quizzes at stacks where the solver
 * never min-raises (only jams or limps), which would produce 0 eligible hands
 * and break the quiz. Keyed by `${format}_${position}`.
 */
const OPEN_RAISE_VALID_STACKS = {
  '3way_BTN': [],        // BTN never min-raises in 3-way (only jams)
  '3way_SB':  [12, 15],  // SB min-raises at 12bb and 15bb in 3-way (both confirmed
                         // against regenerated sb_open CSVs -- see FIXED note above)
  'hu_SB':    [15, 25],  // SB min-raises at 15bb and 25bb in HU
  'hu_BB':    [],        // BB doesn't open-raise in HU
};

export const buildFixedHandsConfig = (format, position, primaryAction, secondaryAction, tertiaryAction) => {
  // Only include stacks where the chosen primary action is actually available in the rules.
  // e.g. 'open_limp' is not available at 2bb/3bb for HU SB (only 'open_jam' exists there).
  const allStacks = STACKS_BY_FORMAT[format]?.[position] ?? [];
  let availableStacks = allStacks.filter(stack => {
    const r = getActionConfig(format, position, stack);
    if (!r) return false;

    let hasPrimary = r.primaryActions?.some(a => a.key === primaryAction);

    // 'open_mixed' special case: jam-only stacks (e.g. 3-way BTN 9/6/4bb, 3-way
    // SB 6/3bb, HU SB 3/2bb) never expose an explicit 'Mixed' button — the
    // config screen auto-selects 'open_jam' since it's the only opening
    // action, skipping the button UI entirely (see getAutoConfig). But the
    // underlying range data for 'open_jam' at those stacks IS already the
    // full/mixed range (100% jam, nothing else to mix in) — there's no
    // missing data, just a UI shortcut. Without this, a Mixed Blinds quiz
    // silently dropped those stacks from availableStacks, which truncated
    // the answer screen's circle range (e.g. showing "Jam 15-12bb" instead
    // of "Jam 15-4bb" for a hand that's actually 100% jam at every stack).
    if (!hasPrimary && primaryAction === 'open_mixed') {
      const openingKeys = (r.primaryActions ?? [])
        .map(a => a.key)
        .filter(k => k.startsWith('open_'));
      if (openingKeys.length === 1 && openingKeys[0] === 'open_jam') hasPrimary = true;
    }
    if (!hasPrimary) return false;

    // A selected secondaryAction (a Response Type that points at its own
    // reaction CSV, e.g. SB's "BTN Raise" option under Jam) must actually be
    // wired up for THIS stack, not just have its primaryAction available.
    // 'Jam' exists as a primary action at every SB stack, but the "BTN Raise"
    // reaction under it (csvDir sb_vs_btn_open) only has real CSVs at 12bb/15bb
    // — public/database/blast/preflop/3_way/sb_vs_btn_open/ has no 9bb/7bb/6bb/
    // 3bb files. Without this check, those stacks passed the filter anyway
    // (only primaryAction was tested), the Fixed Hands circle loader's fetch
    // 404'd on the missing CSV, and `if (!res.ok) return foldOnly` upstream in
    // BlastAnswerScreen.jsx silently rendered that as a genuine 100% Fold —
    // e.g. AKo showing "Never | Fold" at 3bb for "SB Jam vs BTN Open", which
    // is a missing-file default, not solved data.
    if (secondaryAction) {
      const secCfg = r.secondary?.[primaryAction];
      const hasSecondary = secCfg?.options?.some(o => o.key === secondaryAction);
      if (!hasSecondary) return false;
    }

    return true;
  });

  // For open_raise with no secondary: only include stacks where the CSV has
  // actual raise actions (e.g. BTN never min-raises in 3-way at any stack --
  // only jams). Without this, the raise filter would return 0 eligible hands
  // and produce a broken quiz (random hand + empty rangeMap). 3-way SB 12bb/15bb
  // WAS on that broken list too until 2026-07-05 -- the shipped CSVs were
  // regenerated (they'd been silently dropping every raise row into 'allin'),
  // and both stacks are now confirmed to have real raise data.
  if (primaryAction === 'open_raise' && !secondaryAction) {
    const validKey = `${format}_${position}`;
    const validRaiseStacks = OPEN_RAISE_VALID_STACKS[validKey];
    if (validRaiseStacks !== undefined) {
      availableStacks = availableStacks.filter(s => validRaiseStacks.includes(s));
    }
  }
  const referenceStack  = availableStacks[0] ?? null; // largest valid stack

  const rule       = referenceStack ? getActionConfig(format, position, referenceStack) : null;
  const csvDir     = getCsvDir(format, position, primaryAction, secondaryAction, rule, tertiaryAction);
  const filterMode = getFilterMode(primaryAction, secondaryAction, tertiaryAction);

  // ── Derive legacy fields (same as buildBlastConfig) ──────────────────────
  const isReaction = secondaryOverridesCsv(rule, primaryAction, secondaryAction, tertiaryAction);

  let action, subtype, villainPos, villainAction;

  if (primaryAction.startsWith('open_') && !isReaction) {
    action  = 'open';
    subtype = { open_raise: 'raise', open_jam: 'jam', open_limp: 'limp', open_mixed: 'all' }[primaryAction] ?? null;
  } else if (primaryAction === 'bb_vs_sb_call') {
    if (secondaryAction === 'check')       { action = 'check'; subtype = 'check'; }
    else if (secondaryAction === 'jam')    { action = 'raise'; subtype = 'allin'; }
    else if (secondaryAction === 'raise' ||
             secondaryAction === 'raise_large' ||
             secondaryAction === 'raise_small') { action = 'raise'; subtype = secondaryAction; }
    else                                   { action = 'raise'; subtype = 'all'; }
  } else {
    // Reaction / call scenario (explicit, or secondary/tertiary CSV override)
    ({ villainPos, villainAction } = villainFromCsvDir(csvDir ?? ''));
    if (primaryAction === 'open_jam') {
      // SB jammed and villain raised back — hero is deciding whether to jam (call is all-in already)
      action  = 'jam';
      subtype = 'allin';
    } else if (filterMode === 'allin') {
      // User selected "Jam" as their response type — this is a jam-frequency quiz, not a call quiz
      action  = 'jam';
      subtype = filterMode;
    } else {
      action  = 'call';
      subtype = filterMode;
    }
  }

  return {
    format,
    position,
    // Fixed Hands — no single stack; varies per question
    isFixedHands:    true,
    availableStacks,

    // New fields
    primaryAction,
    secondaryAction: secondaryAction ?? null,
    tertiaryAction:  tertiaryAction  ?? null,
    filterMode,
    csvDir,

    // Legacy fields
    action,
    subtype:      filterMode ?? subtype,
    villainPos,
    villainAction,

    // Game config
    quizType:     'BLAST',
    totalHands:   10,
    heroPosition: position,
  };
};

// ─── Random config generation ─────────────────────────────────────────────────

/**
 * Generates a random config for Fixed Hands (Mixed Blinds) mode.
 *
 * Accepts an optional `overrides` object — any field already chosen by the
 * user on the config screen (format, position, primaryAction, secondaryAction,
 * tertiaryAction) is passed in here and locked in as-is; only the remaining
 * blank fields are randomized. This lets "Configure a Random Quiz" fill in
 * whatever's left rather than re-rolling selections the user already made.
 *
 * Rules (for any field NOT supplied via overrides):
 *  - Non-optional secondary: always picked.
 *  - Optional secondary: 50/50.
 *  - If secondary is chosen and has a non-optional tertiary: always picked.
 *  - If secondary is chosen and has an optional tertiary: 50/50.
 *  - Retries if the chosen combination has no valid stacks (e.g. open_raise on BTN).
 *
 * `attempt` guards against infinite recursion in the (should-never-happen)
 * case where the overrides pin a combination that can never have a valid
 * stack — e.g. a manually-forced primaryAction that's always invalid for
 * that format/position (see OPEN_RAISE_VALID_STACKS). Bails out to null
 * after enough retries rather than blowing the call stack.
 *
 * Returns { format, position, primaryAction, secondaryAction, tertiaryAction }
 * or null if no valid combination could be found.
 */
export const createRandomFixedHandsConfig = (overrides = {}, attempt = 0) => {
  if (attempt > 25) return null;

  // Pick random format and position from the Fixed Hands stack map, unless
  // already chosen by the user.
  const formats = Object.keys(STACKS_BY_FORMAT); // ['3way', 'hu']
  const format = (overrides.format && formats.includes(overrides.format))
    ? overrides.format
    : formats[Math.floor(Math.random() * formats.length)];

  const posKeys = Object.keys(STACKS_BY_FORMAT[format]);
  const position = (overrides.position && posKeys.includes(overrides.position))
    ? overrides.position
    : posKeys[Math.floor(Math.random() * posKeys.length)];

  // Find the reference stack (the one with the most primary actions).
  const stacks = STACKS_BY_FORMAT[format][position];
  let bestStack = stacks[0];
  let bestCount = 0;
  for (const s of stacks) {
    const r = getActionConfig(format, position, s);
    const count = r?.primaryActions?.length ?? 0;
    if (count > bestCount) { bestCount = count; bestStack = s; }
  }

  const rule = getActionConfig(format, position, bestStack);
  if (!rule) return createRandomFixedHandsConfig(overrides, attempt + 1); // shouldn't happen

  // Use the user's chosen primary action if present, else pick randomly.
  const primary = (overrides.primaryAction && rule.primaryActions.find(a => a.key === overrides.primaryAction))
    || rule.primaryActions[Math.floor(Math.random() * rule.primaryActions.length)];

  const secCfg = rule.secondary?.[primary.key] ?? null;
  let secondaryAction = overrides.secondaryAction ?? null;
  let tertiaryAction  = overrides.tertiaryAction ?? null;

  if (secCfg) {
    if (secondaryAction == null) {
      // Non-optional secondary must always be filled; optional is 50/50.
      const pickSecondary = !secCfg.isOptional || Math.random() > 0.5;
      if (pickSecondary) {
        const secOpt = secCfg.options[Math.floor(Math.random() * secCfg.options.length)];
        secondaryAction = secOpt.key;
      }
    }

    if (secondaryAction) {
      const secOpt = secCfg.options.find(o => o.key === secondaryAction);
      if (secOpt?.tertiary && tertiaryAction == null) {
        // Non-optional tertiary must always be filled; optional is 50/50.
        const pickTertiary = !secOpt.tertiary.isOptional || Math.random() > 0.5;
        if (pickTertiary) {
          const terOpt = secOpt.tertiary.options[Math.floor(Math.random() * secOpt.tertiary.options.length)];
          tertiaryAction = terOpt.key;
        }
      }
    }
  }

  // Validate: the combo must have at least one valid stack in Fixed Hands mode.
  const test = buildFixedHandsConfig(format, position, primary.key, secondaryAction, tertiaryAction);
  if (test.availableStacks.length === 0) {
    return createRandomFixedHandsConfig(overrides, attempt + 1);
  }

  return { format, position, primaryAction: primary.key, secondaryAction, tertiaryAction };
};

/**
 * Picks a random raw Fixed Blinds selection — { format, position, stack,
 * primaryAction, secondaryAction, tertiaryAction } — without building the
 * final config object. Used both by createRandomBlastConfig (which builds
 * the full config for the range screen) and directly by the config screen
 * (which needs the raw fields to populate its own SelectionGroup state).
 *
 * Accepts an optional `overrides` object — any field already chosen by the
 * user on the config screen is locked in as-is; only the remaining blank
 * fields are randomized.
 *
 * If the given overrides don't match any real scenario (shouldn't happen —
 * the config screen only ever offers valid combos), the most specific
 * override is progressively relaxed rather than failing outright. Returns
 * null if truly no scenario exists at all.
 */
export const createRandomBlastSelection = (overrides = {}) => {
  const allKeys = Object.keys(BLAST_RANGE_RULES);
  const matches = allKeys.filter(key => {
    const [fmt, pos, stackStr] = key.split('_');
    if (overrides.format && fmt !== overrides.format) return false;
    if (overrides.position && pos !== overrides.position) return false;
    if (overrides.stack != null && parseInt(stackStr) !== overrides.stack) return false;
    return true;
  });

  if (matches.length === 0) {
    if (overrides.stack != null) return createRandomBlastSelection({ ...overrides, stack: undefined });
    if (overrides.position)      return createRandomBlastSelection({ ...overrides, position: undefined });
    if (overrides.format)        return createRandomBlastSelection({ ...overrides, format: undefined });
    return null; // truly no scenarios exist — shouldn't happen
  }

  const key   = matches[Math.floor(Math.random() * matches.length)];
  const [fmt, pos, stackStr] = key.split('_');
  const stack = parseInt(stackStr);
  const rule  = BLAST_RANGE_RULES[key];

  // Use the user's chosen primary action if present, else pick randomly.
  const primary = (overrides.primaryAction && rule.primaryActions.find(a => a.key === overrides.primaryAction))
    || rule.primaryActions[Math.floor(Math.random() * rule.primaryActions.length)];

  const secCfg = rule.secondary?.[primary.key];
  let secondaryAction = overrides.secondaryAction ?? null;
  let tertiaryAction  = overrides.tertiaryAction ?? null;

  if (secCfg) {
    if (secondaryAction == null) {
      // Non-optional secondary must always be filled; optional is 50/50.
      const pickSecondary = !secCfg.isOptional || Math.random() > 0.5;
      if (pickSecondary) {
        const secOpt = secCfg.options[Math.floor(Math.random() * secCfg.options.length)];
        secondaryAction = secOpt.key;
      }
    }

    if (secondaryAction) {
      const secOpt = secCfg.options.find(o => o.key === secondaryAction);
      if (secOpt?.tertiary && tertiaryAction == null &&
          (!secOpt.tertiary.isOptional || Math.random() > 0.5)) {
        const terOpt = secOpt.tertiary.options[Math.floor(Math.random() * secOpt.tertiary.options.length)];
        tertiaryAction = terOpt.key;
      }
    }
  }

  return { format: fmt, position: pos, stack, primaryAction: primary.key, secondaryAction, tertiaryAction };
};

/**
 * Generates a full random Fixed Blinds config (built via buildBlastConfig).
 * Thin wrapper around createRandomBlastSelection — kept for callers that
 * need the finished config object rather than the raw selection fields
 * (e.g. createPositionBlastConfig's fallback).
 */
export const createRandomBlastConfig = (overrides = {}) => {
  const sel = createRandomBlastSelection(overrides);
  if (!sel) return null;
  return buildBlastConfig(sel.format, sel.position, sel.stack, sel.primaryAction, sel.secondaryAction, sel.tertiaryAction);
};

/**
 * Generates a Fixed Blinds (single stack) config targeting a specific hero position.
 * Used by the Recommended Quiz card on the dashboard.
 * Falls back to a fully random config if no valid key is found.
 */
export const createPositionBlastConfig = (position) => {
  const keys = Object.keys(BLAST_RANGE_RULES).filter(k => k.split('_')[1] === position);
  if (keys.length === 0) return createRandomBlastConfig();

  const key = keys[Math.floor(Math.random() * keys.length)];
  const [fmt, pos, stackStr] = key.split('_');
  const stack = parseInt(stackStr);
  const rule  = BLAST_RANGE_RULES[key];

  const primary = rule.primaryActions[Math.floor(Math.random() * rule.primaryActions.length)];

  // Always fill non-optional secondary; skip optional ones for simplicity
  const secCfg = rule.secondary?.[primary.key] ?? null;
  let secondaryAction = null;
  if (secCfg && !secCfg.isOptional) {
    const secOpt = secCfg.options[Math.floor(Math.random() * secCfg.options.length)];
    secondaryAction = secOpt.key;
  }

  return buildBlastConfig(fmt, pos, stack, primary.key, secondaryAction, null);
};

/**
 * Generates a Mixed Blinds (fixed hands / multi-stack) config targeting a specific hero position.
 * Used by the Recommended Quiz card on the dashboard.
 * Falls back to a fully random Fixed Hands config if no valid combo is found.
 */
export const createPositionFixedHandsConfig = (position) => {
  const formats = Object.keys(STACKS_BY_FORMAT).filter(fmt => !!STACKS_BY_FORMAT[fmt][position]);
  if (formats.length === 0) return createRandomFixedHandsConfig();

  const format = formats[Math.floor(Math.random() * formats.length)];

  // Find reference stack (most primary actions)
  const stacks = STACKS_BY_FORMAT[format][position];
  let bestStack = stacks[0], bestCount = 0;
  for (const s of stacks) {
    const r = getActionConfig(format, position, s);
    const count = r?.primaryActions?.length ?? 0;
    if (count > bestCount) { bestCount = count; bestStack = s; }
  }

  const rule = getActionConfig(format, position, bestStack);
  if (!rule) return createRandomFixedHandsConfig();

  const primary = rule.primaryActions[Math.floor(Math.random() * rule.primaryActions.length)];

  // Always fill non-optional secondary
  const secCfg = rule.secondary?.[primary.key] ?? null;
  let secondaryAction = null;
  if (secCfg && !secCfg.isOptional) {
    const secOpt = secCfg.options[Math.floor(Math.random() * secCfg.options.length)];
    secondaryAction = secOpt.key;
  }

  const config = buildFixedHandsConfig(format, position, primary.key, secondaryAction, null);
  if (config.availableStacks.length === 0) return createRandomFixedHandsConfig();
  return config;
};

// ─── Scenario node (dashboard drill targeting) ────────────────────────────────

/**
 * Parses a scenario_node string written by deriveScenarioNode (src/utils/handUtils.js)
 * for a BLAST-mode quiz back into its component selections.
 *
 * Returns null for non-BLAST nodes (legacy strings, cash-game nodes, or null).
 */
export const parseScenarioNode = (node) => {
  if (!node || typeof node !== 'string' || !node.startsWith('BLAST|')) return null;

  const [, format, stackPart, position, primaryAction, secondaryAction, tertiaryAction] = node.split('|');
  if (!format || !position || !primaryAction) return null;

  return {
    format,
    stack: stackPart && stackPart !== 'mixed' ? parseInt(stackPart, 10) : null,
    position,
    primaryAction,
    secondaryAction: secondaryAction || null,
    tertiaryAction:  tertiaryAction  || null,
  };
};

/**
 * Returns a sensible default stack for a format+position when no stack-specific
 * performance data is available (e.g. a scenario only ever seen via Mixed Blinds).
 * Uses the largest / most common stack for that position, matching the ordering
 * already used throughout STACKS_BY_FORMAT.
 */
export const getDefaultStackForPosition = (format, position) => {
  const stacks = STACKS_BY_FORMAT[format]?.[position];
  return stacks && stacks.length > 0 ? stacks[0] : null;
};
